import { Injectable, signal, inject } from '@angular/core';
import { OidcAuthService } from '@app/core/services/oidc-auth.service';
import { HubConnection, HubConnectionBuilder, HttpTransportType } from '@microsoft/signalr';
import { Observable } from 'rxjs';
import { EnvironmentService } from '@app/core/services/environment.service';
// import { environment } from '../../environments/environment';
// import { AuthService } from '../auth/auth.service';      // existing shell service
// import { TenantService } from '../tenant/tenant.service'; // existing shell service

export interface ChatStreamChunk {
  type: 'started' | 'token' | 'citation' | 'action_proposal' | 'error' | 'done';
  conversationId?: string;
  content?: string;
  pendingActionId?: string;
  messageId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChatBotService {
  // private auth = inject(AuthService);
  // private tenant = inject(TenantService);
  private connection: HubConnection | null = null;

  readonly connected = signal(false);

  constructor(
    private oidcAuthService: OidcAuthService,
    private envService: EnvironmentService
  ) {}

  private getHubUrl(): string {
    const baseUrl = (this.envService.env.apiUrl ?? '').replace(/\/+$/, '');
    return `${baseUrl}/hubs/chat`;
  }

  async connect(): Promise<void> {
    if (!this.oidcAuthService.isAuthenticated()) {
      throw new Error('Not authenticated — sign in before connecting to the chat hub.');
    }

    if (this.connection) return;

    const tenantId = this.oidcAuthService.getTenantId() || this.envService.env.tenantId;

    this.connection = new HubConnectionBuilder()
      .withUrl(this.getHubUrl(), {
        accessTokenFactory: () => {
          if (!this.oidcAuthService.isAuthenticated()) {
            return '';
          }

          return this.oidcAuthService.accessToken;
        },
        // The negotiate request is HTTP, so headers are available there even when the
        // actual transport is WebSockets. Forcing LongPolling causes the POST
        // /hubs/chat?id=... poll endpoint, which returns 404 when the hub is not set up
        // for that transport.
        transport: HttpTransportType.WebSockets,
        headers: tenantId ? { TenantId: tenantId } : undefined,
        withCredentials: false // CORS is AllowAnyOrigin — that rejects credentialed requests
      })
      .withAutomaticReconnect()
      .build();

    await this.connection.start();
    this.connected.set(true);
  }

  async disconnect(): Promise<void> {
    await this.connection?.stop();
    this.connection = null;
    this.connected.set(false);
  }

  /** conversationId: null starts a new conversation; pass back the id from 'started' after that. */
  sendMessage(message: string, conversationId: string | null): Observable<ChatStreamChunk> {
    if (!this.connection) throw new Error('Not connected — call connect() first.');
    const connection = this.connection;

    return new Observable<ChatStreamChunk>((subscriber) => {
      const sub = connection
        .stream<ChatStreamChunk>('SendMessage', message, conversationId)
        .subscribe({
          next: (chunk) => subscriber.next(chunk),
          error: (err) => subscriber.error(err),
          complete: () => subscriber.complete()
        });
      return () => sub.dispose();
    });
  }
}